/**
 * Practice Enrollments section types
 *
 * Supports: enrollment overview (provider × payer), application progress lifecycle,
 * "billing through" relationships. Aligns with Notion UI - Practice Enrollments
 * and credentialing SOP (Therapist Onboarding During Credentialing).
 *
 * Lifecycle states: Not started | In progress | Submitted | Pending payer response | Approved | Denied.
 * Assumption: optional substages (e.g. Primary Source Verification) can be shown as "substatus" or in detail view.
 */

export type EnrollmentStatus =
  | 'not-started'
  | 'in-progress'
  | 'submitted'
  | 'pending-payer-response'
  | 'approved'
  | 'denied';

export type EnrollmentType = 'individual' | 'group' | 'billing-through';

export interface Enrollment {
  id: string;
  providerId: string;
  providerName: string;
  payerId: string;
  payerName: string;
  enrollmentType: EnrollmentType;
  status: EnrollmentStatus;
  /** True when status is approved and can bill */
  billingReady: boolean;
  /** Current stage label for display (e.g. "Primary Source Verification") */
  currentStage?: string;
  /** Days in current stage; negative = overdue */
  daysInStage?: number;
  /** Required next action (e.g. "Upload W-9") */
  nextAction?: string;
  /** Blockers (missing docs, payer request) */
  blockers?: string[];
  submittedAt?: string;
  submittedBy?: string;
  updatedAt: string;
  updatedBy?: string;
  /** Effective date when approved */
  effectiveDate?: string;
  /** Billing-through: entity ID/name when provider bills under another */
  billingThroughEntityId?: string;
  billingThroughEntityName?: string;
}

export interface BillingThroughRelationship {
  id: string;
  providerId: string;
  providerName: string;
  billingEntityId: string;
  billingEntityName: string;
  effectiveStart: string;
  effectiveEnd?: string;
  payersImpacted: string[];
  /** Warnings (e.g. expiring, missing contract) */
  warnings?: string[];
}

/** Options for new enrollment form (provider/payer/entity dropdowns) */
export interface ProviderOption {
  id: string;
  name: string;
}
export interface PayerOption {
  id: string;
  name: string;
}
export interface BillingEntityOption {
  id: string;
  name: string;
}

export type EnrollmentFilterStatus = 'all' | EnrollmentStatus;

export interface EnrollmentsFilters {
  providerSearch: string;
  payerSearch: string;
  status: EnrollmentFilterStatus;
  billingReadyOnly: boolean;
  /** Sort: risk (blockers/stalled first), age (oldest in stage first), or default */
  sortBy: 'default' | 'risk' | 'age';
}

export const defaultEnrollmentsFilters: EnrollmentsFilters = {
  providerSearch: '',
  payerSearch: '',
  status: 'all',
  billingReadyOnly: false,
  sortBy: 'default',
};

export const EnrollmentStatusLabels: Record<EnrollmentStatus, string> = {
  'not-started': 'Not started',
  'in-progress': 'In progress',
  submitted: 'Submitted',
  'pending-payer-response': 'Pending payer response',
  approved: 'Approved',
  denied: 'Denied',
};

export const EnrollmentStatusColors: Record<EnrollmentStatus, string> = {
  'not-started': 'gray',
  'in-progress': 'blue',
  submitted: 'cyan',
  'pending-payer-response': 'yellow',
  approved: 'green',
  denied: 'red',
};
